import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ControlContainer, FormControl, ReactiveFormsModule } from '@angular/forms';
import { IonSelect, IonSelectOption } from '@ionic/angular/standalone';
import {
  effectiveOptionalRequired,
  explicitPlaceholder,
  withOptionalLabelSuffix,
} from '../form-optional-display';

@Component({
  selector: 'crowdctrl-form-select-field',
  standalone: true,
  imports: [IonSelect, IonSelectOption, ReactiveFormsModule],
  templateUrl: './form-select-field.component.html',
  styleUrls: [
    '../form-outline-field-tokens.css',
    '../form-control-hint.css',
    './form-select-field.component.css',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FormSelectFieldComponent implements OnChanges {
  @Input() options: Array<{ value: any; label: string }> = [];
  @Input() invalid = false;
  @Input() hint = '';
  @Input() controlName?: string;
  @Input() label?: string;
  @Input() placeholder?: string;
  /**
   * When true: appends ` (optional)` to the label (unless already present) and clears the
   * required affordance. Use a **value-hint** {@link placeholder} (e.g. “Select …”) — never the
   * word “Optional” as the placeholder.
   */
  @Input() optional = false;
  @Input() mode: 'md' | 'ios' = 'md';
  @Input() fill: 'outline' | 'solid' = 'outline';
  @Input() interface: 'alert' | 'action-sheet' | 'popover' | 'modal' = 'popover';
  @Input() interfaceOptions: Record<string, any> = {};
  @Input() required = false;
  @Input() disabled = false;
  @Input() multiple = false;
  @Input() compareWith?: (o1: any, o2: any) => boolean;
  @Input() cancelText?: string;
  @Input() okText?: string;
  @Input() selectedText?: string;
  @Output() ionChange = new EventEmitter<CustomEvent<any>>();
  @Output() ionBlur = new EventEmitter<CustomEvent<any>>();
  @Output() ionFocus = new EventEmitter<CustomEvent<any>>();

  protected formControl: FormControl | null = null;

  get effectiveLabel(): string | undefined {
    return withOptionalLabelSuffix(this.optional, this.label);
  }

  get effectivePlaceholder(): string | undefined {
    return explicitPlaceholder(this.placeholder);
  }

  get effectiveRequired(): boolean {
    return effectiveOptionalRequired(this.required, this.optional);
  }

  constructor(private readonly controlContainer: ControlContainer) {}

  ngOnChanges(): void {
    this.formControl = this.resolveFormControl();
    if (this.disabled && this.formControl?.enabled) {
      this.formControl.disable({ emitEvent: false });
    }
  }

  private resolveFormControl(): FormControl | null {
    if (!this.controlName) {
      return null;
    }

    const control = this.controlContainer.control?.get(this.controlName);
    return control instanceof FormControl ? control : null;
  }
}
